import React from "react";
import ReactDOM from "react-dom/client";
import {
    createBrowserRouter,
    Navigate,
    RouterProvider,
} from "react-router-dom";
import TestRoute from "./components/TestRoute";
import { AuthProvider } from "./context/AuthContext";
import { TestProvider } from "./context/TestContext";

import "./index.css";
import reportWebVitals from "./reportWebVitals";
import { ROUTES, TEST_PAGE_PREFIX } from "./routes/config";
import Login from "./routes/auth-flow/login";
import OpenTest from "./routes/open-test/open-test";
import Layout from "./components/layouts/layout";
import DataInput from "./routes/test/data-input";
import Interpretation from "./routes/test/interpretation";
import AnaerobicThreshold from "./routes/test/interpretation/anaerobic-threshold";
import Inbar12 from "./routes/test/interpretation/inbar-12";
import SummaryTable from "./routes/test/interpretation/summary-tables";
import Wasserman9 from "./routes/test/interpretation/wasserman-9";
import ConfirmContextProvider from "./context/ConfirmContextProvider";
import InterpretationProvider from "./context/InterpretationContext";
import { ReportPage } from "./routes/test/report";
import AlertContextProvider from "./context/AlertContextProvider";
import AuthFlowLayout from "./components/layouts/auth-flow-layout";
import ResetPasswordPage from "./routes/auth-flow/reset-password";
import RoutesGuard from "./routes/RoutesGuard";
import { USER_ROLES } from "./types";
import LocalAdminDashboard from "./routes/local-admin-dashboard";
import { ReportProvider } from "./hooks/useReport";
import { GraphEnlargeProvider } from "./context/GraphEnlargeContext";
import LogoutPage from "./routes/auth-flow/logout";
import PageNotFound from "./routes/PageNotFound";

const router = createBrowserRouter([
    // Auth Routes
    {
        path: "/",
        element: (
            <AuthProvider>
                <RoutesGuard forUserAuthType="logged-out">
                    <AuthFlowLayout />
                </RoutesGuard>
            </AuthProvider>
        ),
        children: [
            { index: true, element: <Navigate to={ROUTES.LOGIN.path} /> },
            { path: ROUTES.LOGIN.path, element: <Login /> },
            {
                path: ROUTES.RESET_PASSWORD.path,
                element: <ResetPasswordPage />,
            },
        ],
    },

    {
        path: `/${ROUTES.LOGOUT.path}`,
        element: (
            <AuthProvider>
                <RoutesGuard forUserAuthType="logged-in">
                    <LogoutPage />
                </RoutesGuard>
            </AuthProvider>
        ),
    },
    // App Routes
    {
        path: "/",
        element: (
            <AuthProvider>
                <RoutesGuard
                    forUserAuthType="logged-in"
                    disallowedRoles={[USER_ROLES.LOCAL_ADMIN]}
                >
                    <ReportProvider>
                        <Layout />
                    </ReportProvider>
                </RoutesGuard>
            </AuthProvider>
        ),
        children: [
            {
                path: ROUTES.OPEN_TEST.path,
                element: <OpenTest />,
            },
            {
                path: TEST_PAGE_PREFIX,
                element: (
                    <ConfirmContextProvider>
                        <InterpretationProvider>
                            <TestProvider>
                                <GraphEnlargeProvider>
                                    <TestRoute />
                                </GraphEnlargeProvider>
                            </TestProvider>
                        </InterpretationProvider>
                    </ConfirmContextProvider>
                ),
                children: [
                    { index: true, element: <Navigate to="data-input" /> },
                    {
                        path: ROUTES.DATA_INPUT.path,
                        element: <DataInput />,
                    },
                    {
                        path: ROUTES.INTERPRETATION.path,
                        children: [
                            {
                                index: true,
                                element: <Interpretation />,
                            },
                            {
                                path: ROUTES.INTERPRETATION.subroutes
                                    .SUMMARY_TABLE.path,
                                element: <SummaryTable />,
                            },
                            {
                                path: ROUTES.INTERPRETATION.subroutes.INBAR12
                                    .path,
                                element: <Inbar12 />,
                            },
                            {
                                path: ROUTES.INTERPRETATION.subroutes
                                    .WASSWERMAN9.path,
                                element: <Wasserman9 />,
                            },
                            {
                                path: ROUTES.INTERPRETATION.subroutes
                                    .ANAEROBIC_THRESHOLD.path,
                                element: <AnaerobicThreshold />,
                            },
                        ],
                    },
                    {
                        path: ROUTES.REPORT.path,
                        element: <ReportPage />,
                    },
                ],
            },
            {
                path: "*",
                element: <PageNotFound />,
            },
        ],
    },
    // Local Admin Dashboard
    {
        path: `/${ROUTES.LOCAL_ADMIN_DASHBOARD.path}`,
        element: (
            <AuthProvider>
                <ConfirmContextProvider>
                    <RoutesGuard
                        forUserAuthType="logged-in"
                        allowedRoles={[USER_ROLES.LOCAL_ADMIN]}
                    >
                        <Layout />
                    </RoutesGuard>
                </ConfirmContextProvider>
            </AuthProvider>
        ),
        children: [{ index: true, element: <LocalAdminDashboard /> }],
    },
]);

const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
);
root.render(
    <React.StrictMode>
        <AlertContextProvider>
            <RouterProvider router={router} />
        </AlertContextProvider>
    </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
